import { useEffect, useRef, useState } from 'react';
import { Sidebar, useWindowDimensions } from 'ui';

import { useProductList } from '@/modules/product/context/productList';
import { CategoryThreeItem, MenuItem } from '@/shared/api/endpoints/menu';
import { MultilevelSidebar } from '@/shared/components/header/general/multilevelSidebar';
import { routes } from '@/shared/data/routes';
import { useCurrentLocale } from '@/shared/utils/client';

import styles from './CategoryThreeSidebar.module.scss';

export const CategoryThreeSidebar = () => {
  const { width } = useWindowDimensions();
  const {
    productMenuMobileSidebarOpen,
    setProductMenuMobileSidebarOpen,
    categoryThree,
    categorySlug,
  } = useProductList();
  const [open, setOpen] = useState<string[]>([]);
  const locale = useCurrentLocale();
  const [formattedCategoryThree] = useState<MenuItem[]>(() => {
    const formatArrayForMultilevelSidebar = (
      threeItem: CategoryThreeItem[]
    ): MenuItem[] => {
      return threeItem.map((item) => ({
        children: Array.isArray(item?.children)
          ? formatArrayForMultilevelSidebar(item.children)
          : [],
        label: item.name,
        url: `${routes[locale].PRODUCTS_LIST}/${item.slug}`,
        position: 0,
      }));
    };

    const formattedArray = formatArrayForMultilevelSidebar(categoryThree);

    return formattedArray;
  });
  const navRef = useRef<HTMLDivElement>(null);

  const clearedSlug = categorySlug.includes('%')
    ? categorySlug.split('%')[0]
    : categorySlug.split(',')[0];

  const findCategory =
    (
      slug: string,
      depth: number,
      index: number,
      parentId: string,
      openPath: string[]
    ) =>
    ([head, ...tail]: CategoryThreeItem[]): string[] | undefined => {
      if (head === undefined) return undefined;

      const id = `${parentId}${depth}${index}`;

      if (slug === head.slug) {
        const result = [...openPath];

        if (head.children) {
          result.push(id);
        }

        return result;
      }

      if (head.children) {
        const _openPath = [...openPath];
        _openPath.push(id);
        const childrenResult = findCategory(
          slug,
          depth + 1,
          0,
          id,
          _openPath
        )(head.children);
        if (childrenResult) return childrenResult;
      }

      return findCategory(slug, depth, index + 1, parentId, openPath)(tail);
    };

  const closeSidebar = () => {
    setProductMenuMobileSidebarOpen(false);
  };

  useEffect(() => {
    const initialCategory = findCategory(
      clearedSlug,
      1,
      0,
      '00',
      []
    )(categoryThree);

    setOpen(initialCategory ?? []);
  }, []);

  useEffect(() => {
    if (!navRef.current) {
      return;
    }

    if (open.length === 1) {
      navRef.current.style.overflowY = 'hidden';
      return;
    }

    if (open.length === 0) {
      navRef.current.style.overflowY = 'auto';
    }
  }, [open, width]);

  useEffect(() => {
    if (!productMenuMobileSidebarOpen) {
      return;
    }

    if (width < 1280) {
      return;
    }

    setProductMenuMobileSidebarOpen(false);
  }, [width, productMenuMobileSidebarOpen]);

  return (
    <Sidebar
      side="left"
      isOpen={productMenuMobileSidebarOpen}
      onOverlayPress={closeSidebar}
      wrapperClassName={styles.categoryThreeSidebar}
    >
      <nav className={styles.categoryThreeSidebar__nav} ref={navRef}>
        <MultilevelSidebar
          showAll={true}
          showFirstLevelImage={false}
          items={formattedCategoryThree}
          open={open}
          itemClass={styles.categoryThreeSidebar__three}
          setOpen={setOpen}
        />
      </nav>
    </Sidebar>
  );
};
